import { ExportGapFilterType } from "@app/exports/enumerations";
import { FC, useContext, useMemo } from "react";
import { VolumeReportsContext } from "./VolumeReportsContext";
import { UserContext } from "@app/user/UserContext";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { QESRadioGroup } from "@app/shared/qes-standards/forms/RadioGroup";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { isSpecialtySelectionValid } from "@app/reports/volume-reports/import/specialtySelection";
import Roles from "@app/user/roles";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";

export const VolumeReportsSpecialtiesFilters: FC<React.PropsWithChildren<unknown>> = () => {
  const wizardContext = useContext(VolumeReportsContext);
  const userContext = useContext(UserContext);
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);

  const isNotZipReport = wizardContext.reportType?.toString() !== "zip-summary" && wizardContext.reportType?.toString() !== "zip-class-aggregate";
  const isZipProvidersReport = wizardContext.reportType === "zip-providers";

  const showFillableByMultiple = useMemo(() => {
    return userContext.roles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER);
  }, [userContext.roles]);

  const specialtySelectionValid = isSpecialtySelectionValid({
    includeFacilitySpecialties: wizardContext.includeFacilitySpecialties,
    includeInformationalSpecialties: isNotZipReport ? wizardContext.includeInformationalSpecialties : true,
    includePhysicianSpecialties: wizardContext.includePhysicianSpecialties,
    includeRequiredSpecialties: isNotZipReport ? wizardContext.includeRequiredSpecialties : true,
  });

  return (
    <section>
      <h2 className="header-m">Specialties Filter</h2>
      <p>Please select the specialties to include:</p>
      {!specialtySelectionValid && (
        <QESAlert type="warning" persist={true}>
          Select at least one of Physician or Facility specialties{isNotZipReport ? ', and at least one of Required or Informational specialties,' : ''} to generate a report.
        </QESAlert>
      )}

      <p className="mt-4 mb-1">Include</p>
      <QESCheckbox
        label="Physician Specialties"
        testId="volume-reports__include-physician-specialties"
        checked={wizardContext.includePhysicianSpecialties}
        onChange={(e) => { wizardContext.setIncludePhysicianSpecialties(e.target.checked) }}
      />
      <QESCheckbox
        label="Facility Specialties"
        testId="volume-reports__include-facility-specialties"
        checked={wizardContext.includeFacilitySpecialties}
        onChange={(e) => { wizardContext.setIncludeFacilitySpecialties(e.target.checked) }}
      />
      {wizardContext.reportType?.toString() !== "zip-summary" && wizardContext.reportType?.toString() !== "zip-class-aggregate" && (
        <>
          <QESCheckbox
            label="Required Specialties"
            testId="volume-reports__include-required-specialties"
            checked={wizardContext.includeRequiredSpecialties}
            onChange={(e) => { wizardContext.setIncludeRequiredSpecialties(e.target.checked) }}
          />
          <QESCheckbox
            label="Informational Specialties"
            testId="volume-reports__include-informational-specialties"
            checked={wizardContext.includeInformationalSpecialties}
            onChange={(e) => { wizardContext.setIncludeInformationalSpecialties(e.target.checked) }}
          />
        </>
      )}
      <QESRadioGroup
        groupLabel="Include"
        groupContainerClass="mt-4"
      >
        <QESRadioButton
          label="All Specialties"
          name="specialties-to-include"
          checked={wizardContext.specialtyGapFilter === ExportGapFilterType.All}
          testId="volume-reports__specialties-filters--all"
          onChange={(e) => wizardContext.setSpecialtyGapFilter(e.target.value as ExportGapFilterType)}
          value={ExportGapFilterType.All}
        />
        {isZipProvidersReport ? (
          <QESRadioButton
            label="Specialties not meeting access"
            name="specialties-to-include"
            checked={wizardContext.specialtyGapFilter === ExportGapFilterType.NotMeetingAccess}
            testId="volume-reports__specialties-filters--specialties-not-meeting-access"
            onChange={(e) => wizardContext.setSpecialtyGapFilter(e.target.value as ExportGapFilterType)}
            value={ExportGapFilterType.NotMeetingAccess}
          />
        ) : (
          <>
            <QESRadioButton
              label="Specialties with Gaps"
              name="specialties-to-include"
              checked={wizardContext.specialtyGapFilter === ExportGapFilterType.Gaps}
              testId="volume-reports__specialties-filters--specialties-with-gaps"
              onChange={(e) => wizardContext.setSpecialtyGapFilter(e.target.value as ExportGapFilterType)}
              value={ExportGapFilterType.Gaps}
            />
            {wizardContext.reportType?.toString() !== "zip-class-aggregate" && (
              <>
                <QESRadioButton
                  label="Specialties with Gaps Fillable by Single Provider"
                  name="specialties-to-include"
                  checked={wizardContext.specialtyGapFilter === ExportGapFilterType.FillableBySingleProvider}
                  testId="volume-reports__specialties-filters--specialties-fillable-by-single-provider"
                  onChange={(e) => wizardContext.setSpecialtyGapFilter(e.target.value as ExportGapFilterType)}
                  value={ExportGapFilterType.FillableBySingleProvider}
                />
                <QESRadioButton
                  label="Specialties with Exceptions"
                  name="specialties-to-include"
                  checked={wizardContext.specialtyGapFilter === ExportGapFilterType.Exceptions}
                  testId="volume-reports__specialties-filters--specialties-with-exceptions"
                  onChange={(e) => wizardContext.setSpecialtyGapFilter(e.target.value as ExportGapFilterType)}
                  value={ExportGapFilterType.Exceptions}
                />
                {showFillableByMultiple && (
                  <QESRadioButton
                    label="Specialties with Fillable Gaps"
                    name="specialties-to-include"
                    checked={wizardContext.specialtyGapFilter === ExportGapFilterType.FillableGaps}
                    testId="volume-reports__specialties-filters--specialties-with-fillable-gaps"
                    onChange={(e) => wizardContext.setSpecialtyGapFilter(e.target.value as ExportGapFilterType)}
                    value={ExportGapFilterType.FillableGaps}
                  />
                )}
                {(wizardContext.reportType === "specialty"
                  || (wizardContext.reportType === "specialty-summary-zip-code" && enabledFeatureFlags.has(Features.ZIP_GAP_RESOLUTION_STATUS))) && (
                  <QESRadioButton
                    label="Specialties with Gap Resolution Status"
                    name="specialties-to-include"
                    checked={wizardContext.specialtyGapFilter === ExportGapFilterType.GapResolutionStatus}
                    testId="volume-reports__specialties-filters--specialties-with-gap-resolution-status"
                    onChange={(e) => wizardContext.setSpecialtyGapFilter(e.target.value as ExportGapFilterType)}
                    value={ExportGapFilterType.GapResolutionStatus}
                  />
                )}
              </>
            )}
          </>
        )}
      </QESRadioGroup>
    </section>
  );
}